/**
 * Calculating autosize for input and textarea elements.
 * Setup and calculations borrowed from vue-input-autowidth but adjusted to size height on textarea elements as well.
 * @see https://github.com/syropian/vue-input-autowidth/tree/master
 */

import { debounce } from '@/utils/debounce';

const copyStylesToMirror = (el) => {
  const elTag = el.tagName.toLocaleUpperCase();
  const isTextarea = elTag === 'TEXTAREA';
  const styles = window.getComputedStyle(el);
  const { options } = el;
  const { maxWidth, maxHeight } = options;
  const dynamicStyles = {
    maxWidth,
    maxHeight,
  };

  if (maxWidth && maxWidth.includes('%')) {
    dynamicStyles.maxWidth = `${el.parentElement.offsetWidth / 100 * parseInt(maxWidth.replace('%', ''), 10)}px`;
  }
  if (maxHeight && maxHeight.includes('%')) {
    dynamicStyles.maxHeight = `${el.parentElement.offsetHeight / 100 * parseInt(maxHeight.replace('%', ''), 10)}px`;
  }

  if (!options.autoWidth) {
    dynamicStyles.width = `${el.offsetWidth}px`;
  }

  Object.assign(el.mirror.style, {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    overflow: 'hidden',
    whiteSpace: isTextarea ? 'pre-line' : 'pre',
    overflowWrap: isTextarea ? 'break-word' : 'normal',
    fontSize: styles.fontSize,
    fontFamily: styles.fontFamily,
    fontWeight: styles.fontWeight,
    fontStyle: styles.fontStyle,
    letterSpacing: styles.letterSpacing,
    textTransform: styles.textTransform,
    paddingRight: `calc(${options.comfortZone} + ${styles.paddingRight} + ${styles.borderRightWidth})`,
    paddingLeft: `calc(${styles.paddingLeft} + ${styles.borderLeftWidth})`,
    paddingTop: `calc(${styles.paddingTop} + ${styles.borderTopWidth})`,
    paddingBottom: `calc(${styles.paddingBottom} + ${styles.borderBottomWidth})`,
    ...dynamicStyles,
  });
};

const checkWidth = (el) => {
  const elTag = el.tagName.toLocaleUpperCase();
  const isTextarea = elTag === 'TEXTAREA';
  const { mirror, options } = el;

  if (isTextarea) {
    el.style.overflow = 'auto';
  }

  el.style.maxWidth = options.maxWidth;
  el.style.minWidth = options.minWidth;
  el.style.minHeight = options.minHeight;
  el.style.maxHeight = options.maxHeight;

  let val = el.value;

  if (!val) {
    val = el.placeholder || '';
  }

  while (mirror.childNodes.length) {
    mirror.removeChild(mirror.childNodes[0]);
  }

  mirror.appendChild(document.createTextNode(val));

  const newWidth = mirror.scrollWidth + 2;
  const newHeight = mirror.scrollHeight;

  if (options.autoWidth && newWidth !== el.offsetWidth) {
    el.style.width = `${newWidth}px`;
  }

  if (options.autoHeight && isTextarea && newHeight !== el.offsetHeight) {
    el.style.height = `${newHeight}px`;
  }

  if (isTextarea) {
    el.style.overflow = 'hidden';
  }
};


const copyStylesAndCheckWidth = (el) => {
  copyStylesToMirror(el);
  checkWidth(el);
};

const defaults = {
  autoWidth: true,
  autoHeight: true,
  maxWidth: 'none',
  minWidth: 'none',
  minHeight: 'none',
  maxHeight: 'none',
  comfortZone: '0px',
  watchWindowSize: false,
  windowResizeHandlerDebounceTime: 150,
};

export default {
  bind(el, binding) {
    const elTag = el.tagName.toLocaleUpperCase();
    if (!['INPUT', 'TEXTAREA'].includes(elTag)) {
      throw new Error('v-input-autosize can only be used on input or textarea elements.');
    }

    el.dataset.uuid = Math.random().toString(36).slice(-5);
    el.style.boxSizing = 'content-box';
    el.options = Object.assign({}, defaults, binding.value);
  },

  inserted(el, binding, vnode) {
    const hasVModel = vnode.data.directives.some(
      directive => directive.name === 'model'
    );

    el.sizerFunction = (updateStyling = false) => {
      if (updateStyling) {
        copyStylesAndCheckWidth(el);
      } else {
        checkWidth(el);
      }

      const doneEvent = new Event('autosize:done');
      el.dispatchEvent(doneEvent);
    };

    el.mirror = document.createElement('div');
    copyStylesToMirror(el);
    el.mirror.classList.add(`vue-input-autowidth-mirror-${el.dataset.uuid}`);
    el.mirror.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el.mirror);

    copyStylesAndCheckWidth(el, binding);

    if (!hasVModel) {
      el.addEventListener('input', el.sizerFunction);
    }

    if (el.options.watchWindowSize && el.options.windowResizeHandlerDebounceTime !== undefined) {
      const windowResizeHandler = () => copyStylesAndCheckWidth(el);
      el.windowResizeHandler = debounce(windowResizeHandler, el.options.windowResizeHandlerDebounceTime);
      window.addEventListener('resize', el.windowResizeHandler, { passive: true });
    }
  },

  componentUpdated(el, binding) {
    const optionsUpdated = JSON.stringify(binding.value) !== JSON.stringify(binding.oldValue);

    if (optionsUpdated) {
      el.options = Object.assign({}, defaults, binding.value);
    }

    if (el.sizerFunction) {
      el.sizerFunction(optionsUpdated);
    }
  },

  unbind(el) {
    document.body.removeChild(el.mirror);

    if (el.sizerFunction) {
      el.removeEventListener('input', el.sizerFunction);
    }

    if (el.options.watchWindowSize && el.windowResizeHandler) {
      window.removeEventListener('resize', el.windowResizeHandler);
    }
  }
};
